/* eslint-disable @typescript-eslint/no-explicit-any -- handlers formulaire génériques */
import type { TFunction } from 'i18next';
import U2000CheckBox from '../../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import U2000InfoTooltip from '../../../u2000Core/components/formControls/feedback/U2000InfoTooltip';
import U2000InputMemo from '../../../u2000Core/components/formControls/input/U2000InputMemo';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import type { U2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import type { U3202ModelCapabilities } from '../../utils/U3202CapabilitiesTypes';
import U3202CapabilitiesBadges from '../common/U3202CapabilitiesBadges';

interface U3202CatalogModelFieldsProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  data: Record<string, unknown> | null;
  t: TFunction;
  NS: string;
  readonly?: boolean;
  showCodeFields?: boolean;
  codeFieldName?: string;
  nameFieldName?: string;
  showCatalogActive?: boolean;
}

function U3202FieldLabelWithTooltip({
  label,
  tooltip,
  required,
}: {
  label: string;
  tooltip: string;
  required?: boolean;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required ? <span className="ml-1 text-red-500">*</span> : null}
      </span>
      <U2000InfoTooltip message={tooltip} align="left" />
    </div>
  );
}

function U3202CheckBoxWithTooltip({
  formStateHandlers,
  fieldName,
  label,
  tooltip,
  readonly,
}: {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  label: string;
  tooltip: string;
  readonly?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <U2000CheckBox
        formStateHandlers={formStateHandlers}
        fieldName={fieldName}
        label={label}
        readonly={readonly}
      />
      <U2000InfoTooltip message={tooltip} align="left" />
    </div>
  );
}

function U3202CatalogModelFields({
  formStateHandlers,
  data,
  t,
  NS,
  readonly = false,
  showCodeFields = true,
  codeFieldName = 'code',
  nameFieldName = 'name',
  showCatalogActive = true,
}: U3202CatalogModelFieldsProps) {
  const memoHandlers = {
    ...formStateHandlers,
    getData: () => data,
    setData: (row: Record<string, unknown>) => formStateHandlers.setData(row),
  };

  const capabilities = (data?.capabilities as U3202ModelCapabilities | undefined) ?? {};

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {showCodeFields ? (
        <>
          <U2000InputText
            formStateHandlers={formStateHandlers as any}
            fieldName={codeFieldName}
            label={t('catalogWizard.lblModelCode', { ns: NS })}
            required
            readonly={readonly}
          />
          <U2000InputText
            formStateHandlers={formStateHandlers as any}
            fieldName={nameFieldName}
            label={t('lblModel', { ns: NS })}
            required
            readonly={readonly}
          />
        </>
      ) : null}

      <div className="md:col-span-2">
        <U3202FieldLabelWithTooltip
          label={t('lblParametersJson', { ns: NS })}
          tooltip={t('catalogModel.parametersJsonHelp', { ns: NS })}
        />
        <U2000InputMemo
          formStateHandlers={memoHandlers as any}
          fieldName="parametersJson"
          label=""
          hideLabel
          rows={8}
          minHeight="10rem"
          readonly={readonly}
          placeholder='{"temperature": 0.7, "max_tokens": 4096}'
        />
      </div>

      <div className="md:col-span-2">
        <U3202FieldLabelWithTooltip
          label={t('capabilities.sectionLabel', { ns: NS })}
          tooltip={t('capabilities.sectionHelp', { ns: NS })}
        />
        <U3202CapabilitiesBadges capabilities={capabilities} t={t} ns={NS} />
      </div>

      <div className="flex flex-col gap-2 md:col-span-2">
        {showCatalogActive ? (
          <U3202CheckBoxWithTooltip
            formStateHandlers={formStateHandlers as any}
            fieldName="active"
            label={t('activatedModels.active', { ns: NS })}
            tooltip={t('catalogModel.catalogActiveHelp', { ns: NS })}
            readonly={readonly}
          />
        ) : null}
      </div>
    </div>
  );
}

export default U3202CatalogModelFields;
